import { createContext, useReducer, useEffect } from 'react';

export const AuthContext = createContext();

export const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN':
      return { user: action.payload };
    case 'LOGOUT':
      // Clear localStorage when logging out
      localStorage.removeItem('user');
      return { user: null };
    case 'UPDATE_COMMUNITIES':
      const newState = { user: { ...state.user, listOfCommunities: action.payload } };
      return newState;
    case 'REFRESH_USER':
      return { user: action.payload };
    default:
      return state;
  }
};

export const AuthContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
  });

  const setUser = (user) => {
    dispatch({ type: 'LOGIN', payload: user });
  };

  const updateCommunities = (communities) => {
    console.log('🔄 [AUTH DEBUG] updateCommunities called with:', communities);
    console.log('🔄 [AUTH DEBUG] Previous communities:', state.user?.listOfCommunities);
    
    dispatch({ type: 'UPDATE_COMMUNITIES', payload: communities });

    // Update local storage
    const currentUser = JSON.parse(localStorage.getItem('user'));
    if (currentUser) {
      console.log('🔄 [AUTH DEBUG] Updating localStorage with new communities');
      currentUser.listOfCommunities = communities;
      localStorage.setItem('user', JSON.stringify(currentUser));
    }
  };

  const refreshUserProfile = async () => {
    try {
      const auth = await import('firebase/auth').then(m => m.getAuth());
      const token = await auth.currentUser?.getIdToken(true);
      
      if (!token) {
        console.warn('🔄 [AUTH DEBUG] No token available for profile refresh');
        return;
      }

      console.log('🔄 [AUTH DEBUG] Refreshing user profile from server');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userProfile/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('🔄 [AUTH DEBUG] Profile refreshed successfully');
        
        // Update both state and localStorage with fresh data
        dispatch({ type: 'REFRESH_USER', payload: userData.profile });
        localStorage.setItem('user', JSON.stringify(userData.profile));
      } else {
        console.error('🔄 [AUTH DEBUG] Failed to refresh profile:', response.status);
      }
    } catch (error) {
      console.error('🔄 [AUTH DEBUG] Error refreshing profile:', error);
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));

    if (user) {
      setUser(user);
    }
  }, []);

  // Count a visit when a signed-in member opens the site — at most every 30
  // minutes (admin → Community Analytics, "Visits").
  const signedInId = state.user?.userId;
  useEffect(() => {
    if (!signedInId) return;
    let last = 0;
    try { last = Number(localStorage.getItem('ic_lastVisitPing')) || 0; } catch { /* storage blocked */ }
    if (Date.now() - last < 30 * 60 * 1000) return;
    (async () => {
      try {
        const { getAuth, onAuthStateChanged } = await import('firebase/auth');
        const auth = getAuth();
        const fbUser = auth.currentUser || await new Promise((resolve) => {
          const off = onAuthStateChanged(auth, (u) => { off(); resolve(u); });
        });
        if (!fbUser) return;
        const token = await fbUser.getIdToken();
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/userProfile/visit`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
        try { localStorage.setItem('ic_lastVisitPing', String(Date.now())); } catch { /* ignore */ }
      } catch { /* not counted this time */ }
    })();
  }, [signedInId]);

  console.log('AuthContext state:', state);

  return (
    <AuthContext.Provider value={{ ...state, dispatch, setUser, updateCommunities, refreshUserProfile }}>
      {children}
    </AuthContext.Provider>
  );
};